import * as React from 'react';

import EmailPaymentModal from 'payments/PaymentDepositModal/EmailPaymentModal';
import { REMOTE_DEPOSIT_ADDED_EVENT } from 'payments/PaymentDepositModal/RemoteDepositAddedEvent';
import Format from 'root/apps/app/src/data/Format';
import {
  LSPaymentHardwareSetupStatus,
  useLightspeedPaymentsSetupStatus,
} from 'root/apps/hooks/useLightspeedPaymentsSetupStatus';
import { AddDepositModal } from 'root/apps/QuoteMachineIntegration/components/AddDepositModal/AddDepositModal';
import DepositInstructionService from 'root/apps/QuoteMachineIntegration/services/v1/DepositInstructionService';
import Modules from 'root/apps/shared_modules/Modules';

import AddPaymentDepositModal from './AddPaymentDepositModal';
import CancelDepositModal from './CancelDepositModal';
import { useRemoteDepositPaymentLinkActions } from './hooks/useRemoteDepositPaymentLinkActions';
import RequestDepositPaymentLink from './RequestDepositPaymentLinkModal';
import { ModalProps } from './types';

const AddPaymentDeposit: React.FC<ModalProps> = ({
  onHide,
  onSubmit,
  openOrders,
  saleManager,
  isAccountPayment,
}) => {
  const [amount, setAmount] = React.useState<string>('');
  const [radioBtnOption, setRadioBtnOption] = React.useState('in-person-deposit');
  const [paylinkCardOption, setPaylinkCardOption] = React.useState('by-email');
  const [isModalOpen, setIsModalOpen] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const [currentShopHasLightspeedPayments, setCurrentShopHasLightspeedPayments] =
    React.useState(false);
  const [modalView, setModalView] = React.useState('add-deposit');
  const [previousModalView, setPreviousModalView] = React.useState('');

  const {
    status: lightspeedPaymentsSetupStatus,
    isError: isErrorFetchingLightspeedPaymentsSetupStatus,
  } = useLightspeedPaymentsSetupStatus();
  // TODO: Replace 'invoicing.api.v1.deposit_instructions' with 'invoicing.api.v1' once deposit
  // instructions are fully rolled out to all merchants. The separate module flag allows gradual
  // rollout of the deposit instruction feature.
  const depositInstructionsModuleEnabled = Modules.hasModule(
    'invoicing.api.v1.deposit_instructions'
  );
  const invoicingModalEnabled = Modules.hasModule('customers.invoicing_integration.free_tier');
  const disableInvoicingInPersonDeposit = !!saleManager.depositPaymentOptions;

  /**
   * Handles the deposit instruction API call for in-person deposits.
   * - DepositInstructionService handles creating credit account on R-Series if needed
   * - Calls PUT deposit-instruction endpoint to record the deposit instruction
   * - If 404, DepositInstructionService will create the credit account on Invoicing
   *
   * @param depositAmount The deposit amount
   * @returns The credit account ID (existing or newly created)
   */
  const handleDepositInstruction = async (depositAmount: number): Promise<string> => {
    const customerId = saleManager.customer.attributes.customerID;
    const creditAccountId = saleManager.customer.attributes.creditAccountID;
    const currency = window.merchantos.locale.currency_code;

    // Call deposit instruction service - it handles R-Series and Invoicing credit account creation
    // Cast to any since runtime attributes may contain Contact/ContactAddresses fields
    // that aren't in the TypeScript interface
    const result = await DepositInstructionService.upsertDepositInstruction({
      creditAccountId: creditAccountId || 0,
      customerId: `${customerId}`,
      saleId: `${saleManager.sale.id}`,
      amount: depositAmount,
      currency,
      customer: saleManager.customer.attributes as any,
    });

    return result.creditAccountId;
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onHide();
  };

  const { isError, addRemoteDepositPaymentRequest } =
    useRemoteDepositPaymentLinkActions(saleManager.sale.id) || {};

  const handleSubmitAddRemoteDepositPaymentRequest = async (sendEmail?: boolean) => {
    if (addRemoteDepositPaymentRequest) {
      await addRemoteDepositPaymentRequest(amount, sendEmail || false);
    } else {
      window.alertUser('Something went wrong. Try again.');
    }

    if (!isError) {
      handleCloseModal();
      window.dispatchEvent(new CustomEvent(REMOTE_DEPOSIT_ADDED_EVENT));
    }
  };

  React.useEffect(() => {
    if (
      lightspeedPaymentsSetupStatus ===
        LSPaymentHardwareSetupStatus.INCOMPLETE_GATEWAY_ACTIVE_TERMINAL_UNPAIRED ||
      lightspeedPaymentsSetupStatus === LSPaymentHardwareSetupStatus.TRANSACTIONAL
    ) {
      setCurrentShopHasLightspeedPayments(true);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [lightspeedPaymentsSetupStatus]);

  return (
    <>
      {modalView === 'add-deposit' && !invoicingModalEnabled ? (
        <AddPaymentDepositModal
          openOrders={openOrders}
          saleManager={saleManager}
          isModalOpen={isModalOpen}
          handleCloseModal={handleCloseModal}
          isAccountPayment={isAccountPayment}
          isLoading={isLoading}
          currentShopHasLightspeedPayments={currentShopHasLightspeedPayments}
          setModalView={setModalView}
          setAmount={setAmount}
          amount={amount}
          radioBtnOption={radioBtnOption}
          setRadioBtnOption={setRadioBtnOption}
          isErrorFetchingLightspeedPaymentsStatus={isErrorFetchingLightspeedPaymentsSetupStatus}
          setPaylinkCardOption={setPaylinkCardOption}
        />
      ) : null}
      {modalView === 'add-deposit' && invoicingModalEnabled ? (
        <AddDepositModal
          transactionId={`${saleManager.sale.id}`}
          onCancel={handleCloseModal}
          onSubmit={(options) => {
            if (options.depositType === 'remote') {
              setModalView('request-deposit-paylink');
              setAmount(options.amount.toString());
              return;
            }

            handleCloseModal();
            window.showNotifier('working');
            const amount = Format.parseDecimal(`${options.amount}`, 0);

            // If deposit instructions module is enabled, call the deposit instruction API first
            // Otherwise, go directly to addInSaleDeposit (legacy flow)
            if (depositInstructionsModuleEnabled) {
              handleDepositInstruction(options.amount)
                .then(() => saleManager.addInSaleDeposit(amount))
                .catch(() => {
                  window.showNotifier('warning', 'Error adding the deposit to the sale.');
                })
                .finally(() => {
                  window.hideNotifier('working');
                  if (onSubmit) {
                    onSubmit();
                  }
                });
            } else {
              // Legacy flow - direct call to addInSaleDeposit
              saleManager
                .addInSaleDeposit(amount)
                .catch(() => {
                  window.showNotifier('warning', 'Error adding the deposit to the sale.');
                })
                .finally(() => {
                  window.hideNotifier('working');
                  if (onSubmit) {
                    onSubmit();
                  }
                });
            }
          }}
          onError={() => {}}
          disableRemoteDeposit={!currentShopHasLightspeedPayments}
          disableInPersonDeposit={disableInvoicingInPersonDeposit}
        />
      ) : null}
      {modalView === 'request-deposit-paylink' ? (
        <RequestDepositPaymentLink
          isModalOpen={isModalOpen}
          isLoading={isLoading}
          setModalView={setModalView}
          setPreviousModalView={setPreviousModalView}
          setPaylinkCardOption={setPaylinkCardOption}
          paylinkCardOption={paylinkCardOption}
          handleSubmit={handleSubmitAddRemoteDepositPaymentRequest}
          isError={isError ?? false}
        />
      ) : null}
      {modalView === 'cancel-deposit' ? (
        <CancelDepositModal
          isModalOpen={isModalOpen}
          handleCloseModal={handleCloseModal}
          setModalView={setModalView}
          previousModalView={previousModalView}
        />
      ) : null}
      {modalView === 'add-payment-deposit-email' ? (
        <EmailPaymentModal
          isModalOpen={isModalOpen}
          setModalView={setModalView}
          setPreviousModalView={setPreviousModalView}
          hasLightSpeedPayments
          amount={amount}
          saleManager={saleManager}
          handleSubmitRemoteDepositPaymentRequest={handleSubmitAddRemoteDepositPaymentRequest}
          isError={isError ?? false}
        />
      ) : null}
    </>
  );
};

export default AddPaymentDeposit;
